import { useTranslation } from 'react-i18next'
import { Loader2, Clock, AlertCircle, X, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { useStyles } from '@/hooks/useStyles'
import { tDynamic } from '@/i18n'
import { localizeName } from '@/lib/i18n-content'
import type { QueueItem } from '@/types/queue'

interface QueuePlaceholderCardProps {
  item: QueueItem
  /** 큐 내 위치 (1-based) */
  position: number
  /** 활성 큐 총 개수 */
  totalInQueue: number
  /** pending 아이템 취소 핸들러 */
  onCancel?: () => void
  /** failed 아이템 재시도 핸들러 */
  onRetry?: () => void
}

// aspectRatio 문자열을 CSS aspect-ratio 값으로 변환
function getAspectRatioStyle(ratio: string): React.CSSProperties {
  const [w, h] = ratio.split(':').map(Number)
  if (w && h) {
    return { aspectRatio: `${w}/${h}` }
  }
  return { aspectRatio: '1/1' }
}

// 이미지 개수와 비율에 따라 동적으로 그리드 클래스 결정
function getGridClass(count: number, aspectRatio: string): string {
  const [w, h] = aspectRatio.split(':').map(Number)
  const isPortrait = h > w

  if (count === 1) {
    return isPortrait
      ? 'grid-cols-1 max-w-[200px]'
      : 'grid-cols-1 max-w-sm'
  }
  if (count === 2) {
    return isPortrait
      ? 'grid-cols-2 max-w-md'
      : 'grid-cols-2'
  }
  if (isPortrait) {
    return count <= 3
      ? 'grid-cols-3'
      : 'grid-cols-3 sm:grid-cols-4'
  }
  return count <= 4
    ? 'grid-cols-2 sm:grid-cols-4'
    : 'grid-cols-3 sm:grid-cols-4'
}

export function QueuePlaceholderCard({
  item,
  position,
  totalInQueue,
  onCancel,
  onRetry,
}: QueuePlaceholderCardProps) {
  const { t } = useTranslation()
  const { data: styles } = useStyles()
  const { params, status, progress, progressKey, error } = item

  const getStyleName = (styleId: string | null): string | null => {
    if (!styleId) return null
    const style = styles?.find((s) => s.id === styleId)
    return (style && localizeName(style)) || styleId
  }

  // 상태별 chip 스타일
  const statusChip = {
    processing: {
      className: 'bg-primary/10 text-primary',
      icon: <Loader2 className="w-3 h-3 animate-spin" />,
      text: tDynamic(progressKey) ?? '',
    },
    pending: {
      className: 'bg-amber-100 text-amber-700',
      icon: <Clock className="w-3 h-3" />,
      text: t('history.queuePending', { position, total: totalInQueue }),
    },
    failed: {
      className: 'bg-red-100 text-red-700',
      icon: <AlertCircle className="w-3 h-3" />,
      text: t('queue.failed'),
    },
    completed: {
      className: 'bg-emerald-100 text-emerald-700',
      icon: null,
      text: t('history.queueCompleted'),
    },
  }

  const currentStatus = statusChip[status]

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      {/* 헤더: 프롬프트, 스타일/비율 chip, 상태 표시 */}
      <div className="p-4 border-b border-slate-100">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <p className="text-slate-800 line-clamp-2 text-sm">
              {params.prompt}
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              {/* 템플릿 표시 (있을 경우) */}
              {params.templateInfo && (
                <span className="inline-flex items-center text-xs bg-primary/10 text-primary px-2 py-1 rounded-md">
                  {localizeName(params.templateInfo)}
                </span>
              )}
              {/* 스타일 (템플릿 없을 경우) */}
              {!params.templateInfo && getStyleName(params.style) && (
                <span className="inline-flex items-center text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-md">
                  {getStyleName(params.style)}
                </span>
              )}
              {/* 비율 */}
              <span className="inline-flex items-center text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-md">
                {params.aspectRatio}
              </span>
              {/* 상태 chip */}
              <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md font-medium ${currentStatus.className}`}>
                {currentStatus.icon}
                {currentStatus.text}
              </span>
            </div>
          </div>

          {/* 액션 버튼 */}
          <div className="flex items-center gap-2 shrink-0">
            {/* pending 취소 버튼 */}
            {status === 'pending' && onCancel && (
              <button
                onClick={onCancel}
                className="w-6 h-6 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-700 transition-colors"
                title={t('history.removeFromQueue')}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
            {/* failed 재시도 버튼 */}
            {status === 'failed' && onRetry && (
              <Button
                variant="outline"
                size="sm"
                onClick={onRetry}
                className="h-7 text-xs"
              >
                <RotateCcw className="w-3 h-3 mr-1" />
                {t('history.retry')}
              </Button>
            )}
          </div>
        </div>

        {/* 에러 메시지 */}
        {status === 'failed' && error && (
          <div className="mt-3 p-3 bg-red-50 border border-red-100 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* 프로그레스 바 (processing 시) */}
        {status === 'processing' && (
          <div className="mt-3">
            <Progress value={progress} className="h-1.5" indicatorClassName="bg-brand-gradient" />
          </div>
        )}
      </div>

      {/* Placeholder 이미지 그리드 */}
      <div className="p-4">
        <div className={`grid ${getGridClass(params.count, params.aspectRatio)} gap-3`}>
          {Array.from({ length: params.count }).map((_, i) => (
            <div
              key={`placeholder-${item.id}-${i}`}
              className={`relative rounded-lg overflow-hidden ${
                status === 'failed' ? 'bg-red-50' : 'bg-slate-100'
              }`}
              style={getAspectRatioStyle(params.aspectRatio)}
            >
              {/* Shimmer 애니메이션 (processing/pending 시) */}
              {(status === 'processing' || status === 'pending') && (
                <div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent animate-shimmer"
                  style={{ animationDelay: `${i * 0.3}s` }}
                />
              )}
              {/* 이미지 번호 */}
              <div className="absolute inset-0 flex items-center justify-center">
                <span className={`text-3xl font-bold ${
                  status === 'failed' ? 'text-red-200' : 'text-slate-300'
                }`}>
                  #{i + 1}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
